import Vue from 'vue'
import config from '@/config'
import { COURSE_CONVERSION_QUIET_LIMIT_MS } from '@/constant/backgroundTasks.constant'
import { isCourseConversionRunning } from '@/utils/courseConversionCard'
import { nextBackgroundTaskRunId } from './backgroundTasks.module'
import { isEmbedded } from './embeddable/embeddable.utils'

const SET_CARDS = 'SET_CARDS'
const SET_DISMISSED_COURSE_IDS = 'SET_DISMISSED_COURSE_IDS'

// Not in state: timers are not serialisable data. One per course, keyed by lower-cased id.
const quietTimers = new Map()

const sameCourse = (first, second) => typeof first === 'string'
  && typeof second === 'string'
  && first.toLowerCase() === second.toLowerCase()

const timerKey = (courseId) => courseId.toLowerCase()
const findCard = (cards, courseId) => cards.find((card) => sameCourse(card.courseId, courseId))
const cleanTitle = (title) => (typeof title === 'string' ? title.trim() : '')

const _state = {
  // One card per conversion the master agent runs: [{ courseId, courseTitle, stage, runId }].
  cards: [],
  // A running conversion the user closed stays hidden until it ends or a new hand-off starts.
  dismissedCourseIds: [],
}

const _getters = {
  canUseCourseConversionTask: (_, __, ___, rootGetters) => !!rootGetters['featureToggles/canUseCourseConversionTask'],
  // The app-wide stack draws the cards; an embedded presenter (Teams, Zoom…) shows no background-task cards.
  rendersCourseConversionCard: (_, getters) => getters.canUseCourseConversionTask && !isEmbedded(),
  courseConversionCards: (state) => state.cards,
}

const replaceCard = (cards, courseId, nextCard) => cards.map((card) => (sameCourse(card.courseId, courseId) ? nextCard : card))

const actions = {
  // The hand-off or a progress report may carry no title; the course itself always has one.
  async resolveCourseTitleAction({ commit, state }, courseId) {
    const card = findCard(state.cards, courseId)
    if (!card || card.courseTitle || !Vue.http) return
    try {
      const response = await Vue.http.get(`${config.api.getCourseTree}${encodeURIComponent(courseId)}/tree`)
      const title = cleanTitle(response && response.body && response.body.content && response.body.content.title)
      const current = findCard(state.cards, courseId)
      if (title && current && !current.courseTitle) commit(SET_CARDS, replaceCard(state.cards, courseId, { ...current, courseTitle: title }))
    } catch {
      // The card keeps its generic heading.
    }
  },

  // The master agent accepted a convert_presentation hand-off: the card shows at once, before its first report.
  startCourseConversionTaskAction({
    commit, dispatch, getters, state,
  }, { courseId, courseTitle = '' }) {
    if (!getters.rendersCourseConversionCard || typeof courseId !== 'string') return
    commit(SET_DISMISSED_COURSE_IDS, state.dismissedCourseIds.filter((id) => !sameCourse(id, courseId)))
    const fresh = {
      courseId, courseTitle: cleanTitle(courseTitle), stage: 'reading', runId: nextBackgroundTaskRunId(),
    }
    const others = state.cards.filter((card) => !sameCourse(card.courseId, courseId))
    commit(SET_CARDS, [...others, fresh])
    dispatch('rearmQuietLimitAction', courseId)
    dispatch('resolveCourseTitleAction', courseId)
  },

  reportCourseConversionProgressAction({
    commit, dispatch, state, getters,
  }, { courseId, stage, courseTitle } = {}) {
    if (!getters.rendersCourseConversionCard || typeof courseId !== 'string') return
    if (state.dismissedCourseIds.some((id) => sameCourse(id, courseId))) {
      if (!isCourseConversionRunning(stage)) commit(SET_DISMISSED_COURSE_IDS, state.dismissedCourseIds.filter((id) => !sameCourse(id, courseId)))
      return
    }
    const title = cleanTitle(courseTitle)
    const card = findCard(state.cards, courseId)
    if (card) {
      const isRerun = !isCourseConversionRunning(card.stage) && isCourseConversionRunning(stage)
      commit(SET_CARDS, replaceCard(state.cards, courseId, {
        ...card,
        stage,
        courseTitle: title || card.courseTitle,
        runId: isRerun ? nextBackgroundTaskRunId() : card.runId,
      }))
    } else if (isCourseConversionRunning(stage)) {
      // A conversion the assistant started from its own chat, with no hand-off to seed the card.
      commit(SET_CARDS, [...state.cards, {
        courseId, courseTitle: title, stage, runId: nextBackgroundTaskRunId(),
      }])
    } else {
      return
    }
    dispatch('rearmQuietLimitAction', courseId)
    dispatch('resolveCourseTitleAction', courseId)
  },

  // A course change from the assistant is a sign of life too, as in aha-elearning.
  noteCourseConversionActivityAction({ dispatch, state }, { kind, id, courseId } = {}) {
    if (kind !== 'course' && kind !== 'lesson') return
    const running = state.cards.filter((card) => isCourseConversionRunning(card.stage))
    const ownerCourseId = kind === 'course' ? id : courseId
    const ownerlessLesson = kind === 'lesson' && typeof courseId !== 'string'
    if (ownerlessLesson) {
      if (running.length === 1) dispatch('rearmQuietLimitAction', running[0].courseId)
      return
    }
    running.forEach((card) => {
      if (sameCourse(card.courseId, ownerCourseId)) dispatch('rearmQuietLimitAction', card.courseId)
    })
  },

  rearmQuietLimitAction({ commit, state }, courseId) {
    const key = timerKey(courseId)
    clearTimeout(quietTimers.get(key))
    quietTimers.delete(key)
    const card = findCard(state.cards, courseId)
    if (!card || !isCourseConversionRunning(card.stage)) return
    const { runId } = card
    quietTimers.set(key, setTimeout(() => {
      quietTimers.delete(key)
      const current = findCard(state.cards, courseId)
      if (current && current.runId === runId) commit(SET_CARDS, replaceCard(state.cards, courseId, { ...current, stage: 'failed' }))
    }, COURSE_CONVERSION_QUIET_LIMIT_MS))
  },

  dismissCourseConversionTaskAction({ commit, state }, courseId) {
    const card = findCard(state.cards, courseId)
    if (!card) return
    if (isCourseConversionRunning(card.stage)) commit(SET_DISMISSED_COURSE_IDS, [...state.dismissedCourseIds, card.courseId])
    const key = timerKey(card.courseId)
    clearTimeout(quietTimers.get(key))
    quietTimers.delete(key)
    commit(SET_CARDS, state.cards.filter((existing) => existing !== card))
  },
}

const mutations = {
  [SET_DISMISSED_COURSE_IDS](state, courseIds) {
    state.dismissedCourseIds = courseIds
  },
  [SET_CARDS](state, cards) {
    state.cards = cards
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
